/*
 * db-gallery-dots: Cotopaxi-style pagination dots for the PDP mobile gallery.
 *
 * Context: on media_layout: grid|collage|list the vendor <product-slider> (assets/product.js)
 * renders a native horizontal scroll-snap strip on mobile (<1068px) and a static 2-up grid on
 * desktop. The vendor pagination is a prev/next arrow + "1/8" counter (.product-images-buttons),
 * which reads cheap; Cotopaxi uses dots only. This element draws one dot per VISIBLE slide under the
 * strip, tracks the active slide with an IntersectionObserver, and scrolls to a slide on tap. It is a
 * progressive enhancement: no vendor JS is touched, and if this script fails the native strip still swipes.
 *
 * Why a MutationObserver is load-bearing: the PDP renders one slide per product media on load (all of
 * them), then the variant-image app (Rubik) asynchronously REMOVES the slides that do not belong to the
 * selected variant, collapsing e.g. 48 slides to 4. That mutation lands AFTER connectedCallback, and again
 * on every colour/variant change. Counting slides only at build time (or only on resize) captured the
 * pre-filter 48. So we watch the gallery container subtree for childList + class/style changes and rebuild
 * the dots whenever the visible-slide set settles. The container is observed (not the strip) so a wholesale
 * strip re-render by the app is caught too; the strip is re-resolved from the container on every build.
 *
 * "Visible" = a .product-images__slide that is still in the DOM and laid out (clientWidth > 0). Removed
 * slides are gone; display:none slides measure 0. This is the same test product.js uses for its own
 * sliderItemsToShow, so dots and the vendor's slide math agree.
 */
if (!customElements.get('db-gallery-dots')) {
  class DBGalleryDots extends HTMLElement {
    connectedCallback() {
      this.host = this.closest('.product-image-container') || document;
      this.slider = this._resolveSlider();
      if (!this.slider) return;

      this._onClick = this._onClick.bind(this);
      this.addEventListener('click', this._onClick);

      // Rebuild whenever the container subtree changes (Rubik add/remove slides, class/style toggles),
      // on resize (breakpoint / layout), and on the theme's variant event (belt and suspenders).
      // Ignore mutations that originate inside this element itself (our own dot writes and class
      // toggles are descendants of the observed container; without this guard every _setActive would
      // re-trigger the observer and schedule a needless rebuild on each scroll).
      this._mo = new MutationObserver((records) => {
        if (records.every((r) => this.contains(r.target))) return;
        this._scheduleBuild();
      });
      this._mo.observe(this.host === document ? document.body : this.host, {
        childList: true,
        subtree: true,
        attributes: true,
        attributeFilter: ['class', 'style']
      });
      this._ro = new ResizeObserver(() => this._scheduleBuild());
      this._ro.observe(this.host === document ? document.documentElement : this.host);
      this._onVariantChange = () => this._scheduleBuild();
      document.addEventListener('product:variant-change', this._onVariantChange);

      this._build();
    }

    disconnectedCallback() {
      this.removeEventListener('click', this._onClick);
      document.removeEventListener('product:variant-change', this._onVariantChange);
      if (this._mo) this._mo.disconnect();
      if (this._ro) this._ro.disconnect();
      if (this._io) this._io.disconnect();
      clearTimeout(this._buildTimer);
    }

    _resolveSlider() {
      const scope = this.host === document ? document : this.host;
      return scope.querySelector('.product-images') || document.getElementById(this.dataset.slider || 'Product-Slider');
    }

    _scheduleBuild() {
      // Debounce: mutations arrive in bursts as the app rewrites the strip; build once it settles.
      clearTimeout(this._buildTimer);
      this._buildTimer = setTimeout(() => this._build(), 150);
    }

    _visibleSlides() {
      if (!this.slider || !this.slider.isConnected) this.slider = this._resolveSlider();
      if (!this.slider) return [];
      return Array.from(this.slider.querySelectorAll('.product-images__slide')).filter(
        (el) => el.clientWidth > 0
      );
    }

    _build() {
      if (this._io) this._io.disconnect();

      this.slides = this._visibleSlides();
      const count = this.slides.length;

      // One image (or none, or strip not ready): no dots.
      if (count < 2) {
        if (this.children.length) this.innerHTML = '';
        this.hidden = true;
        return;
      }
      this.hidden = false;

      // Only redraw the dot buttons when the count actually changed, to avoid churn on every mutation.
      if (this.children.length !== count) {
        const frag = document.createDocumentFragment();
        for (let i = 0; i < count; i++) {
          const dot = document.createElement('button');
          dot.type = 'button';
          dot.className = 'db-gallery-dot';
          dot.dataset.index = i;
          dot.setAttribute('aria-label', `Go to image ${i + 1} of ${count}`);
          frag.appendChild(dot);
        }
        this.innerHTML = '';
        this.appendChild(frag);
        this._active = -1;
      }
      this.dots = Array.from(this.children);

      // Active-slide tracking: the slide most in view within the strip wins.
      this._io = new IntersectionObserver(
        (entries) => {
          entries.forEach((entry) => {
            if (!entry.isIntersecting) return;
            const idx = this.slides.indexOf(entry.target);
            if (idx > -1) this._setActive(idx);
          });
        },
        { root: this.slider, threshold: 0.6 }
      );
      this.slides.forEach((s) => this._io.observe(s));

      this._setActive(this._nearestIndex());
    }

    _nearestIndex() {
      const stripCentre = this.slider.scrollLeft + this.slider.clientWidth / 2;
      let best = 0;
      let bestDist = Infinity;
      this.slides.forEach((s, i) => {
        const c = s.offsetLeft + s.clientWidth / 2;
        const d = Math.abs(c - stripCentre);
        if (d < bestDist) {
          bestDist = d;
          best = i;
        }
      });
      return best;
    }

    _setActive(idx) {
      if (idx === this._active) return;
      this._active = idx;
      if (!this.dots) return;
      this.dots.forEach((d, i) => {
        const on = i === idx;
        d.classList.toggle('is-active', on);
        if (on) d.setAttribute('aria-current', 'true');
        else d.removeAttribute('aria-current');
      });
    }

    _onClick(e) {
      const dot = e.target.closest('.db-gallery-dot');
      if (!dot || !this.slides) return;
      const idx = Number(dot.dataset.index);
      const target = this.slides[idx];
      if (!target) return;
      target.scrollIntoView({ inline: 'center', block: 'nearest', behavior: 'smooth' });
      this._setActive(idx);
    }
  }

  customElements.define('db-gallery-dots', DBGalleryDots);
}
